import React from "react";
import { Box, Container, Typography, Paper, Chip, Divider, Grid, Link as MuiLink } from "@mui/material";
import {
  BRAND,
  Section,
  Callout,
  MiniHeader,
  MiniFooter,
  bodyText,
  listSx,
  usePublicNav,
  useContactEmail,
} from "components/PublicLayout";

const EFFECTIVE_DATE = "21 maggio 2026";

const SECTIONS = [
  { id: "introduzione", title: "1. Introduzione e Accettazione" },
  { id: "definizioni", title: "2. Definizioni" },
  { id: "natura", title: "3. Natura del Servizio" },
  { id: "account", title: "4. Account e Requisiti" },
  { id: "ruoli", title: "5. Ruoli e Responsabilità" },
  { id: "codice-paziente", title: "6. Codice Interno Paziente" },
  { id: "prenotazioni", title: "7. Prenotazione e Gestione Corse" },
  { id: "compensi", title: "8. Compensi e Fatturazione" },
  { id: "obblighi-trasportatore", title: "9. Obblighi del Trasportatore" },
  { id: "obblighi-struttura", title: "10. Obblighi della Struttura" },
  { id: "uso-vietato", title: "11. Usi Vietati" },
  { id: "limitazioni", title: "12. Limitazioni di Responsabilità" },
  { id: "manleva", title: "13. Manleva" },
  { id: "ip", title: "14. Proprietà Intellettuale" },
  { id: "sospensione", title: "15. Sospensione e Cessazione" },
  { id: "privacy", title: "16. Privacy e Dati Personali" },
  { id: "diritto", title: "17. Diritto Applicabile e Foro" },
  { id: "modifiche", title: "18. Modifiche ai Termini" },
  { id: "varie", title: "19. Disposizioni Finali" },
  { id: "contatti", title: "20. Contatti" },
];

export default function TermCondition() {
  const goTo = usePublicNav();
  const contactEmail = useContactEmail();

  return (
    <Box sx={{ bgcolor: BRAND.bgSoft, minHeight: "100vh", fontFamily: '"Inter", sans-serif' }}>
      <MiniHeader
        onHome={() => goTo("/")}
        secondaryLabel="Privacy"
        secondaryHref="/privacy-policy"
      />

      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: "96px", md: "120px" },
          pb: { xs: 5, md: 8 },
          background: `linear-gradient(180deg, #ffffff 0%, ${BRAND.bgSoft} 100%)`,
          borderBottom: `1px solid ${BRAND.border}`,
          overflow: "hidden",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -120,
            right: -120,
            width: 360,
            height: 360,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.teal}33 0%, transparent 70%)`,
            filter: "blur(20px)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative" }}>
          <Chip
            label="Termini e condizioni"
            sx={{
              bgcolor: `${BRAND.teal}22`,
              color: BRAND.tealDark,
              fontWeight: 600,
              fontSize: "0.78rem",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              border: `1px solid ${BRAND.teal}55`,
              mb: 2,
            }}
          />
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 800,
              color: BRAND.ink,
              letterSpacing: "-0.025em",
              fontSize: { xs: "2rem", md: "2.75rem" },
              lineHeight: 1.15,
              mb: 1.5,
            }}
          >
            Termini e Condizioni di MedLift
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              color: BRAND.muted,
              fontSize: { xs: "1rem", md: "1.125rem" },
              lineHeight: 1.6,
              maxWidth: 720,
              mb: 2,
            }}
          >
            Le regole che disciplinano l'uso della piattaforma MedLift da parte
            di strutture sanitarie e trasportatori sanitari professionisti.
            Soggetto al diritto svizzero, foro competente in Svizzera.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
            <Chip
              label={`Ultimo aggiornamento: ${EFFECTIVE_DATE}`}
              sx={{
                bgcolor: BRAND.bgPanel,
                color: BRAND.inkSoft,
                border: `1px solid ${BRAND.border}`,
                fontWeight: 500,
                fontSize: "0.8rem",
              }}
            />
            <Chip
              label="Diritto svizzero · CO"
              sx={{
                bgcolor: BRAND.bgPanel,
                color: BRAND.inkSoft,
                border: `1px solid ${BRAND.border}`,
                fontWeight: 500,
                fontSize: "0.8rem",
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={{ xs: 3, md: 5 }}>
          <Grid item xs={12} md={4} lg={3}>
            <Box sx={{ position: { md: "sticky" }, top: { md: 88 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: `1px solid ${BRAND.border}`,
                  bgcolor: BRAND.bgPanel,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 700,
                    color: BRAND.ink,
                    fontSize: "0.85rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 1.5,
                  }}
                >
                  Indice
                </Typography>
                <Box component="nav" aria-label="Indice dei termini e condizioni">
                  {SECTIONS.map((s) => (
                    <MuiLink
                      key={s.id}
                      href={`#${s.id}`}
                      underline="none"
                      sx={{
                        display: "block",
                        py: 0.6,
                        px: 1,
                        borderRadius: 1.5,
                        color: BRAND.body,
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        transition: "all 0.15s ease",
                        "&:hover": {
                          bgcolor: `${BRAND.teal}18`,
                          color: BRAND.ink,
                        },
                      }}
                    >
                      {s.title}
                    </MuiLink>
                  ))}
                </Box>
              </Paper>
            </Box>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                border: `1px solid ${BRAND.border}`,
                bgcolor: BRAND.bgPanel,
              }}
            >
              <Section id="introduzione" title="1. Introduzione e Accettazione">
                <Typography sx={bodyText}>
                  I presenti Termini e Condizioni ("Termini") disciplinano
                  l'accesso e l'utilizzo della piattaforma MedLift —
                  comprensiva di sito web{" "}
                  <MuiLink
                    href="https://medlift.app"
                    sx={{ color: BRAND.tealDark, fontWeight: 600 }}
                  >
                    medlift.app
                  </MuiLink>{" "}
                  e applicazione mobile distribuita tramite Apple App Store e
                  Google Play Store (di seguito il "Servizio").
                </Typography>
                <Typography sx={bodyText}>
                  Registrandoti o utilizzando il Servizio in qualsiasi modo
                  accetti integralmente i presenti Termini e la nostra{" "}
                  <MuiLink
                    href="/privacy-policy"
                    sx={{ color: BRAND.tealDark, fontWeight: 600 }}
                  >
                    Privacy Policy
                  </MuiLink>
                  . Se non accetti i Termini non sei autorizzato a utilizzare
                  il Servizio.
                </Typography>
              </Section>

              <Section id="definizioni" title="2. Definizioni">
                <Box component="ul" sx={listSx}>
                  <li>
                    <strong>MedLift</strong>: la società svizzera che gestisce la
                    piattaforma, con sede in Svizzera.
                  </li>
                  <li>
                    <strong>Struttura</strong>: ospedale, clinica, casa di cura
                    (RSA) o organizzazione sanitaria registrata sulla
                    piattaforma con ruolo di richiedente trasporto.
                  </li>
                  <li>
                    <strong>Trasportatore</strong>: persona fisica o giuridica
                    abilitata al trasporto sanitario non urgente, registrata
                    sulla piattaforma con ruolo di esecutore del servizio.
                  </li>
                  <li>
                    <strong>Codice Interno Paziente</strong>: stringa
                    alfanumerica che la Struttura assegna a ogni paziente per
                    identificarlo nella prenotazione, senza esporre dati
                    anagrafici.
                  </li>
                  <li>
                    <strong>Prenotazione</strong>: richiesta di trasporto creata
                    dalla Struttura e accettata dal Trasportatore tramite il
                    Servizio.
                  </li>
                  <li>
                    <strong>Compenso</strong>: importo concordato tra Struttura
                    e Trasportatore per l'esecuzione di una Prenotazione.
                  </li>
                </Box>
              </Section>

              <Section id="natura" title="3. Natura del Servizio">
                <Callout tone="info" title="MedLift è una piattaforma di intermediazione">
                  MedLift <strong>non eroga servizi di trasporto sanitario</strong>{" "}
                  e <strong>non è parte del contratto di trasporto</strong> tra
                  Struttura e Trasportatore. MedLift fornisce esclusivamente la
                  tecnologia che consente alle parti di incontrarsi, comunicare
                  e organizzare il servizio.
                </Callout>
                <Typography sx={bodyText}>
                  Conseguenze operative:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Il contratto di trasporto si conclude direttamente tra
                    Struttura e Trasportatore al momento dell'accettazione
                    della Prenotazione.
                  </li>
                  <li>
                    MedLift non assume responsabilità per la qualità,
                    tempestività, sicurezza o esito del trasporto effettuato.
                  </li>
                  <li>
                    MedLift non è un vettore, un assicuratore, un
                    intermediario assicurativo, un istituto di pagamento né un
                    fornitore di servizi sanitari.
                  </li>
                </Box>
              </Section>

              <Section id="account" title="4. Account e Requisiti">
                <Typography sx={bodyText}>
                  Per utilizzare il Servizio occorre creare un account
                  professionale. Confermando la registrazione dichiari che:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>Hai almeno 18 anni e la capacità giuridica di agire.</li>
                  <li>
                    Stai agendo per conto di una Struttura sanitaria autorizzata
                    o di un'attività di trasporto sanitario in regola con le
                    normative cantonali e federali svizzere.
                  </li>
                  <li>
                    Le informazioni fornite (anagrafiche, fiscali, documenti
                    professionali) sono veritiere, complete e aggiornate.
                  </li>
                  <li>
                    Custodisci con cura le credenziali di accesso e ci avvisi
                    immediatamente in caso di sospetto utilizzo non autorizzato.
                  </li>
                </Box>
                <Typography sx={bodyText}>
                  Ogni account è personale e non trasferibile. È vietata la
                  creazione di account multipli per lo stesso soggetto.
                </Typography>
              </Section>

              <Section id="ruoli" title="5. Ruoli e Responsabilità">
                <Typography sx={bodyText}>
                  La piattaforma distingue tre ruoli principali:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    <strong>Struttura (customer)</strong>: crea le
                    Prenotazioni, fornisce il Codice Interno Paziente, indica
                    luogo, orario e ausili richiesti, accetta l'offerta del
                    Trasportatore.
                  </li>
                  <li>
                    <strong>Trasportatore (driver / fleetadmin)</strong>:
                    riceve richieste compatibili, formula offerte, accetta
                    Prenotazioni ed esegue il trasporto.
                  </li>
                  <li>
                    <strong>MedLift</strong>: fornisce la tecnologia, mantiene
                    la disponibilità del Servizio nei limiti di legge e
                    contrattuali, garantisce la sicurezza informatica.
                  </li>
                </Box>
              </Section>

              <Section id="codice-paziente" title="6. Codice Interno Paziente">
                <Callout tone="info" title="Nessun nome paziente sulla piattaforma">
                  La Struttura si impegna a identificare ogni paziente
                  esclusivamente tramite un <strong>Codice Interno
                  Paziente</strong>. È <strong>vietato inserire nomi,
                  cognomi, dati anagrafici, codici fiscali, numero AVS,
                  diagnosi o qualsiasi dato sanitario</strong> nei campi della
                  Prenotazione.
                </Callout>
                <Typography sx={bodyText}>
                  Il Codice Interno Paziente è generato in autonomia dalla
                  Struttura secondo le proprie regole interne. La
                  corrispondenza tra Codice e identità del paziente resta sotto
                  la responsabilità esclusiva della Struttura, all'interno dei
                  propri sistemi protetti.
                </Typography>
                <Typography sx={bodyText}>
                  L'inserimento accidentale di dati personali o sanitari nel
                  campo "Note" o in altri campi liberi costituisce violazione
                  dei presenti Termini e può comportare la sospensione
                  immediata dell'account.
                </Typography>
              </Section>

              <Section id="prenotazioni" title="7. Prenotazione e Gestione Corse">
                <Typography sx={bodyText}>Il flusso operativo è il seguente:</Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    La Struttura crea una Prenotazione indicando indirizzo di
                    prelievo, destinazione, orario, ausili medici richiesti e
                    Codice Interno Paziente.
                  </li>
                  <li>
                    I Trasportatori compatibili nelle vicinanze ricevono la
                    richiesta e possono inviare offerte o accettare al prezzo
                    indicato, secondo la configurazione della piattaforma.
                  </li>
                  <li>
                    La Struttura assegna la Prenotazione al Trasportatore
                    prescelto. Da quel momento il contratto si intende
                    perfezionato direttamente tra le due parti.
                  </li>
                  <li>
                    Il Trasportatore esegue il trasporto, aggiornando lo stato
                    nella app (accettata, arrivato, in viaggio, completata).
                  </li>
                  <li>
                    Una Prenotazione può essere cancellata secondo le
                    politiche di cancellazione concordate tra le parti.
                  </li>
                </Box>
              </Section>

              <Section id="compensi" title="8. Compensi e Fatturazione">
                <Callout tone="info" title="Nessun pagamento all'interno dell'app">
                  MedLift <strong>non gestisce pagamenti</strong>: la
                  piattaforma non integra Stripe, PayPal, carte di credito o
                  altri provider finanziari.
                </Callout>
                <Typography sx={bodyText}>
                  Gli importi visualizzati nell'app hanno una funzione
                  esclusivamente <strong>contabile e di rendicontazione
                  interna</strong>: consentono alle Strutture di monitorare il
                  fatturato generato e ai Trasportatori di tenere traccia dei
                  compensi maturati.
                </Typography>
                <Typography sx={bodyText}>
                  Il regolamento finanziario avviene{" "}
                  <strong>direttamente tra Struttura e Trasportatore al di
                  fuori della piattaforma</strong>, tipicamente tramite
                  bonifico bancario, secondo i termini contrattuali concordati
                  bilateralmente (cadenza, scadenze, modalità di fatturazione).
                </Typography>
                <Typography sx={bodyText}>Conseguentemente:</Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Le fatture sono emesse dal Trasportatore alla Struttura,
                    nel rispetto delle norme svizzere in materia di IVA e
                    fatturazione.
                  </li>
                  <li>
                    MedLift non interviene nella riscossione dei crediti e non
                    risponde di pagamenti omessi, ritardati o contestati tra le
                    parti.
                  </li>
                  <li>
                    Le funzioni "Da pagare" / "Pagato" presenti nella
                    piattaforma servono unicamente come strumento di tracking
                    interno alla Struttura.
                  </li>
                </Box>
              </Section>

              <Section id="obblighi-trasportatore" title="9. Obblighi del Trasportatore">
                <Typography sx={bodyText}>
                  Il Trasportatore garantisce e si impegna a:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Possedere tutte le autorizzazioni cantonali e federali
                    necessarie per l'esercizio del trasporto sanitario non
                    urgente in Svizzera.
                  </li>
                  <li>
                    Utilizzare veicoli a norma, regolarmente immatricolati,
                    revisionati e dotati degli ausili medici dichiarati.
                  </li>
                  <li>
                    Mantenere in corso di validità la patente di guida adeguata
                    e l'assicurazione responsabilità civile veicoli.
                  </li>
                  <li>
                    Caricare nella piattaforma copia leggibile dei documenti
                    richiesti (patente, libretto di circolazione, assicurazione)
                    e aggiornarli alla scadenza.
                  </li>
                  <li>
                    Eseguire il trasporto con perizia, diligenza e tempestività
                    professionali, nel rispetto del paziente.
                  </li>
                  <li>
                    Non trattare in alcun modo i dati personali del paziente
                    eventualmente acquisiti durante il servizio per finalità
                    diverse dall'esecuzione del trasporto.
                  </li>
                </Box>
              </Section>

              <Section id="obblighi-struttura" title="10. Obblighi della Struttura">
                <Typography sx={bodyText}>
                  La Struttura garantisce e si impegna a:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Essere autorizzata ad operare quale Struttura sanitaria
                    secondo le norme cantonali.
                  </li>
                  <li>
                    Generare e gestire in autonomia i Codici Interni Paziente,
                    garantendone unicità e tracciabilità nei propri sistemi
                    interni.
                  </li>
                  <li>
                    Non inserire mai dati anagrafici, sanitari o identificativi
                    del paziente in alcun campo della piattaforma.
                  </li>
                  <li>
                    Fornire al Trasportatore informazioni accurate su luogo,
                    orario e ausili necessari al trasporto.
                  </li>
                  <li>
                    Saldare i compensi al Trasportatore secondo i termini
                    pattuiti, fuori dalla piattaforma.
                  </li>
                  <li>
                    Tenere indenne MedLift da pretese di terzi derivanti
                    dall'inserimento improprio di dati personali sulla
                    piattaforma in violazione della Sez. 6.
                  </li>
                </Box>
              </Section>

              <Section id="uso-vietato" title="11. Usi Vietati">
                <Typography sx={bodyText}>
                  È espressamente vietato:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Utilizzare il Servizio per trasporti d'urgenza o emergenza
                    sanitaria (ambulanze 144). MedLift è destinato
                    esclusivamente al trasporto non urgente.
                  </li>
                  <li>
                    Inserire dati personali, sanitari o sensibili del paziente
                    al di fuori dei campi previsti (in violazione della
                    Sez. 6).
                  </li>
                  <li>
                    Accedere alla piattaforma con strumenti automatizzati,
                    bot, scraping non autorizzato, reverse engineering,
                    decompilazione del codice.
                  </li>
                  <li>
                    Caricare contenuti diffamatori, illegali, offensivi, o che
                    violino diritti di terzi.
                  </li>
                  <li>
                    Aggirare i sistemi di sicurezza, autenticazione o
                    fatturazione interna.
                  </li>
                  <li>
                    Utilizzare il Servizio per attività concorrenti volte a
                    replicare la piattaforma o intercettare gli utenti.
                  </li>
                </Box>
              </Section>

              <Section id="limitazioni" title="12. Limitazioni di Responsabilità">
                <Typography sx={bodyText}>
                  Nei limiti consentiti dal diritto svizzero, MedLift fornisce
                  il Servizio "così com'è" e "secondo disponibilità", senza
                  garanzie esplicite o implicite di adeguatezza a uno scopo
                  specifico, ininterrotta disponibilità o assenza di errori.
                </Typography>
                <Typography sx={bodyText}>
                  MedLift non risponde, in particolare, per:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>
                    Inadempimenti, ritardi, danni o controversie derivanti dal
                    rapporto contrattuale tra Struttura e Trasportatore.
                  </li>
                  <li>
                    Mancato pagamento, ritardi o contestazioni sui compensi
                    regolati fuori piattaforma.
                  </li>
                  <li>
                    Danni indiretti, perdita di profitto, perdita di chance,
                    danno reputazionale.
                  </li>
                  <li>
                    Interruzioni del Servizio dovute a forza maggiore, attacchi
                    informatici, interventi di terzi, manutenzione necessaria.
                  </li>
                </Box>
                <Typography sx={bodyText}>
                  La responsabilità complessiva di MedLift verso un utente,
                  ove non escludibile per legge, è limitata all'importo
                  eventualmente corrispostole dall'utente nei 12 mesi
                  precedenti l'evento. Nulla nei presenti Termini esclude la
                  responsabilità per dolo o colpa grave ai sensi dell'art. 100
                  CO.
                </Typography>
              </Section>

              <Section id="manleva" title="13. Manleva">
                <Typography sx={bodyText}>
                  L'utente si impegna a manlevare e tenere indenne MedLift, i
                  suoi amministratori, dipendenti e collaboratori da qualsiasi
                  pretesa, danno, costo o spesa (incluse spese legali
                  ragionevoli) derivanti da:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>Violazione dei presenti Termini.</li>
                  <li>
                    Violazione di obblighi di legge, in particolare in materia
                    di protezione dei dati personali, sicurezza sanitaria,
                    fiscalità.
                  </li>
                  <li>
                    Pretese avanzate dai pazienti, da loro familiari o tutori,
                    in conseguenza dell'esecuzione del trasporto.
                  </li>
                </Box>
              </Section>

              <Section id="ip" title="14. Proprietà Intellettuale">
                <Typography sx={bodyText}>
                  Tutti i diritti di proprietà intellettuale relativi al
                  Servizio (software, codice sorgente, design, marchi, logo,
                  contenuti del sito) appartengono a MedLift o ai suoi
                  licenzianti.
                </Typography>
                <Typography sx={bodyText}>
                  L'utente riceve unicamente una licenza personale, non
                  esclusiva, non trasferibile e revocabile per utilizzare il
                  Servizio per le finalità qui descritte. È vietato copiare,
                  modificare, distribuire, vendere o sublicenziare qualsiasi
                  parte del Servizio senza autorizzazione scritta di MedLift.
                </Typography>
              </Section>

              <Section id="sospensione" title="15. Sospensione e Cessazione">
                <Typography sx={bodyText}>
                  MedLift può sospendere o chiudere un account, in tutto o in
                  parte, con o senza preavviso, nei seguenti casi:
                </Typography>
                <Box component="ul" sx={listSx}>
                  <li>Violazione dei presenti Termini o di legge.</li>
                  <li>
                    Inserimento di dati personali del paziente in violazione
                    della Sez. 6.
                  </li>
                  <li>Comportamenti fraudolenti, abusivi o lesivi per altri utenti.</li>
                  <li>
                    Mancata risposta a richieste di verifica delle credenziali
                    professionali.
                  </li>
                  <li>Provvedimenti dell'autorità.</li>
                </Box>
                <Typography sx={bodyText}>
                  L'utente può chiudere il proprio account in qualsiasi
                  momento dall'app (Profilo → Impostazioni account → Elimina
                  account) o inviando richiesta scritta a{" "}
                  <MuiLink
                    href={`mailto:${contactEmail}`}
                    sx={{ color: BRAND.tealDark, fontWeight: 600 }}
                  >
                    {contactEmail}
                  </MuiLink>
                  . La cessazione del rapporto non pregiudica le obbligazioni
                  già maturate (es. compensi dovuti, conservazione documenti
                  contabili).
                </Typography>
              </Section>

              <Section id="privacy" title="16. Privacy e Dati Personali">
                <Typography sx={bodyText}>
                  Il trattamento dei dati personali è disciplinato dalla
                  nostra{" "}
                  <MuiLink
                    href="/privacy-policy"
                    sx={{ color: BRAND.tealDark, fontWeight: 600 }}
                  >
                    Privacy Policy
                  </MuiLink>
                  , che costituisce parte integrante dei presenti Termini.
                  L'utilizzo del Servizio implica accettazione della Privacy
                  Policy, conforme alla nuova Legge Federale sulla Protezione
                  dei Dati (nLPD) del 1° settembre 2023.
                </Typography>
              </Section>

              <Section id="diritto" title="17. Diritto Applicabile e Foro">
                <Typography sx={bodyText}>
                  I presenti Termini sono regolati dal{" "}
                  <strong>diritto svizzero</strong>, con esclusione delle
                  norme di conflitto. Per qualsiasi controversia derivante o
                  collegata ai presenti Termini è competente in via esclusiva
                  il <strong>foro della sede di MedLift in Svizzera</strong>,
                  salvi i fori inderogabili previsti dalla legge a tutela del
                  consumatore.
                </Typography>
                <Typography sx={bodyText}>
                  La Convenzione delle Nazioni Unite sui contratti di vendita
                  internazionale di merci (CISG) non si applica.
                </Typography>
              </Section>

              <Section id="modifiche" title="18. Modifiche ai Termini">
                <Typography sx={bodyText}>
                  MedLift si riserva il diritto di modificare i presenti
                  Termini in qualsiasi momento. Le modifiche entrano in vigore
                  alla data indicata in cima al documento. In caso di
                  modifiche sostanziali ti informeremo tramite email o
                  notifica in-app con almeno 30 giorni di preavviso. La
                  prosecuzione dell'utilizzo del Servizio dopo l'entrata in
                  vigore delle modifiche comporta accettazione delle stesse.
                </Typography>
              </Section>

              <Section id="varie" title="19. Disposizioni Finali">
                <Box component="ul" sx={listSx}>
                  <li>
                    <strong>Salvabilità</strong>: se una disposizione dei
                    presenti Termini è dichiarata invalida o inefficace, le
                    restanti disposizioni rimangono pienamente vincolanti.
                  </li>
                  <li>
                    <strong>Rinuncia</strong>: il mancato esercizio di un
                    diritto da parte di MedLift non costituisce rinuncia allo
                    stesso.
                  </li>
                  <li>
                    <strong>Cessione</strong>: MedLift può cedere i propri
                    diritti e obblighi a terzi nell'ambito di operazioni
                    societarie; l'utente non può cedere il proprio account
                    senza preventivo consenso scritto.
                  </li>
                  <li>
                    <strong>Forza maggiore</strong>: nessuna delle parti è
                    responsabile per inadempimenti dovuti a cause di forza
                    maggiore (catastrofi naturali, atti di autorità, attacchi
                    informatici, pandemie).
                  </li>
                  <li>
                    <strong>Comunicazioni</strong>: tutte le comunicazioni
                    ufficiali vengono inviate all'indirizzo email associato
                    all'account; si considerano ricevute quando recapitate sul
                    server di destinazione.
                  </li>
                </Box>
              </Section>

              <Section id="contatti" title="20. Contatti">
                <Typography sx={bodyText}>
                  Per qualsiasi domanda relativa ai presenti Termini puoi
                  scriverci a:
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mt: 1,
                    borderRadius: 3,
                    border: `1px solid ${BRAND.border}`,
                    bgcolor: BRAND.bgSoft,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 600,
                        color: BRAND.ink,
                        fontSize: "1rem",
                      }}
                    >
                      MedLift — Supporto contrattuale
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        color: BRAND.muted,
                        fontSize: "0.9rem",
                        mt: 0.5,
                      }}
                    >
                      Risposta entro 5 giorni lavorativi
                    </Typography>
                  </Box>
                  <MuiLink
                    href={`mailto:${contactEmail}`}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      bgcolor: BRAND.teal,
                      color: BRAND.ink,
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      px: 2.5,
                      py: 1.25,
                      borderRadius: 2,
                      textDecoration: "none",
                      transition: "background 0.15s ease",
                      "&:hover": { bgcolor: BRAND.tealDark },
                    }}
                  >
                    {contactEmail}
                  </MuiLink>
                </Paper>
              </Section>

              <Divider sx={{ my: 4, borderColor: BRAND.border }} />
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  color: BRAND.muted,
                  fontSize: "0.85rem",
                  textAlign: "center",
                }}
              >
                Documento in vigore dal {EFFECTIVE_DATE}. Soggetto al diritto
                svizzero. Versione bilingue disponibile su richiesta.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <MiniFooter contactEmail={contactEmail} onNavigate={goTo} />
    </Box>
  );
}
